// ============================================================================
// "+ TAG" — the chip that used to be a dashed <span> with an apology in its title.
//
// WHY AN INPUT AND NOT A "#" INSERTED INTO THE TEXT. `tags` is a STORED FIELD on the journal
// entry, not something derived from `#hashtags` in the body — packages/shared/src/types.ts →
// `JournalEntry.tags: string[]`, written through `JournalEntryInput.tags?: string[]`
// (apps/web/src/lib/data/types.ts), sent as part of the PUT body by remote.ts, merged by the
// Worker at apps/worker/src/routes/journal.ts (`input.tags !== undefined ? input.tags : existing`)
// and by demo.ts's `saveJournalEntry`. Nothing anywhere parses the entry text looking for tags. So
// typing "#thing" into the composer would produce a hash in the prose and no tag on the entry —
// which is the kind of thing that looks like it worked and did not.
//
// WHAT IT DOES NOT DO, deliberately: no autocomplete, no suggestions, no taxonomy, no rename, and
// no remove. It appends one tag. Everything else is a bigger feature wearing this one's clothes.
//
// THE UNDO CONTRACT IS UNTOUCHED. This is a separate, short-lived input; the composer's textarea
// stays uncontrolled and is never remounted by a tag write (its `key` is the DATE). A tags-only
// save carries NO `text`, and both the Worker and demo keep the stored text when `text` is absent,
// so adding a tag mid-sentence cannot overwrite what is still on the debounce.
// ============================================================================

import { useState, type CSSProperties } from 'react'
import type { ISODate } from '@app/shared'

import { useSaveJournalEntry } from '../../lib/data'

/**
 * The Worker's own ceiling: `z.array(z.string().max(100)).max(50)` on PUT /api/journal/{date}
 * (apps/worker/src/routes/journal.ts). Stated here so the 51st tag is refused with a sentence
 * instead of a 400 nobody sees.
 */
const MAX_TAGS = 50
const MAX_TAG_LENGTH = 100

const CHIP_SHAPE = 'rounded-pill px-3 py-[6px] text-[11.5px] font-medium leading-[1.3]'

/**
 * Trim, and drop the `#` the UI draws for you. That is the whole normalisation: lower-casing,
 * de-spacing or slugifying would be inventing a taxonomy, and the owner's tags are his own.
 */
function normalise(raw: string): string {
  return raw.trim().replace(/^#+/, '').trim()
}

export function AddTagChip({
  date,
  tags,
  label = '+ tag',
}: {
  date: ISODate
  /** The tags already on the entry — the new one is appended to these. */
  tags: string[]
  label?: string
}) {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState('')
  // Its own mutation instance rather than the composer's: this control reports ITS OWN failure,
  // and borrowing the autosave's error state would make a failed tag read as a lost sentence.
  // The day is the mutation's SCOPE as well as its target: this write queues behind the
  // composer's autosave for the same day instead of racing it into the Worker's read-merge-write.
  const save = useSaveJournalEntry(date)

  const full = tags.length >= MAX_TAGS

  // One handler for both the Enter key (submit) and clicking away (blur), which is why it takes
  // the smallest shape both events satisfy rather than a `FormEvent`.
  function commit(event: { preventDefault: () => void }) {
    event.preventDefault()
    const tag = normalise(value)
    setValue('')
    // Nothing typed, or a tag the entry already carries. Either way the entry is already in the
    // state the press asked for, so it closes rather than writing.
    if (!tag || tags.includes(tag)) {
      setOpen(false)
      return
    }
    save.mutate({ date, tags: [...tags, tag] })
    // Stay open: tags arrive in twos and threes, and re-opening the field for each one is the
    // friction the journal is not allowed to add.
    setOpen(true)
  }

  if (!open) {
    return (
      <span className="flex items-center gap-2">
        <button
          type="button"
          disabled={full}
          onClick={() => setOpen(true)}
          title={
            full
              ? `This entry already carries ${MAX_TAGS} tags, which is the API's ceiling for one entry.`
              : 'Adds a tag to this entry. It is saved on the entry itself, not typed into the text.'
          }
          style={{ '--mo-rule': 'var(--color-journal)' } as CSSProperties}
          className={[
            CHIP_SHAPE,
            'mo-ink mo-press border border-dashed border-dash-2 text-muted',
            // §2's disabled rule taken literally: a full entry's chip has no hover, no press and
            // NO CURSOR CHANGE — the `.mo-ink[disabled]` rules in index.css do the rest.
            full ? '' : 'cursor-pointer hover:text-ink-2',
          ].join(' ')}
        >
          {label}
        </button>
        {/* Never silently. The optimistic tag has already been rolled back by the mutation, so
            the chip that was briefly there is gone — this says why instead of leaving the press
            looking like it was ignored. */}
        {save.isError && (
          <span
            role="status"
            title={save.error instanceof Error ? save.error.message : undefined}
            className="text-[10.5px] leading-[1.35] text-danger"
          >
            Tag not saved — press + tag to try again
          </span>
        )}
      </span>
    )
  }

  return (
    <form onSubmit={commit} className="flex items-center">
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setValue('')
            setOpen(false)
          }
        }}
        maxLength={MAX_TAG_LENGTH}
        aria-label="New tag"
        placeholder="tag"
        style={{ '--mo-rule': 'var(--color-journal)' } as CSSProperties}
        className={[
          CHIP_SHAPE,
          'mo-ink w-[104px] border border-dashed border-dash-2 bg-transparent text-ink-2',
          'outline-none placeholder:text-microcopy',
        ].join(' ')}
      />
    </form>
  )
}
